/**
 * Legal document overlay for the landing page — opens Terms of Service /
 * Privacy Policy / Cookies Policy as a "case file" over the marketing screen.
 *
 * Documents come live from the core CMS (GET /api/cms/legal/:slug, edited in
 * the admin panel); `{{company.*}}` placeholders inside them are substituted
 * from the admin-managed legal-entity settings, so a change in Admin → Game
 * Settings shows up here with no copy edits.
 */
import { fetchLegalPage, fillCompanyVars, type CompanyInfo } from '../game/legal-api';

const closers = new WeakMap<HTMLElement, () => void>();

/** Close an open legal overlay (used by the landing teardown so the Esc listener never leaks). */
export function closeLegalDoc(root: HTMLElement): void {
  const wrap = root.querySelector<HTMLElement>('.lglv');
  if (wrap) closers.get(wrap)?.();
}

export function openLegalDoc(
  root: HTMLElement,
  slug: string,
  company: () => Promise<CompanyInfo | null>,
): void {
  // one at a time — re-use the mounted overlay if a doc is already open
  const existing = root.querySelector<HTMLElement>('.lglv');
  if (existing) {
    void loadInto(existing, slug, company);
    return;
  }

  const wrap = document.createElement('div');
  wrap.className = 'lglv';
  wrap.setAttribute('role', 'dialog');
  wrap.setAttribute('aria-modal', 'true');
  wrap.innerHTML =
    '<div class="lglv-back"></div>' +
    '<article class="lglv-doc" tabindex="-1">' +
    '  <span class="lglv-pin" aria-hidden="true">&#9670;</span>' +
    '  <button class="lglv-x" aria-label="Close">&times;</button>' +
    '  <p class="lglv-kicker">Mafinini &middot; Official Papers</p>' +
    '  <h1 class="lglv-title"></h1>' +
    '  <p class="lglv-stamp" aria-hidden="true">Filed with The Commission</p>' +
    '  <div class="lglv-rule"></div>' +
    '  <div class="lglv-body"><p class="lglv-wait">Fetching the paperwork&hellip;</p></div>' +
    '</article>';
  root.appendChild(wrap);

  const close = () => {
    document.removeEventListener('keydown', onKey);
    wrap.remove();
  };
  closers.set(wrap, close);
  const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
  document.addEventListener('keydown', onKey);
  wrap.querySelector('.lglv-back')!.addEventListener('click', close);
  wrap.querySelector('.lglv-x')!.addEventListener('click', close);

  // cross-links between policies inside the document body
  wrap.addEventListener('click', (e) => {
    const link = (e.target as HTMLElement).closest<HTMLElement>('[data-legal]');
    if (!link) return;
    e.preventDefault();
    e.stopPropagation(); // keep the SPA-wide link interceptor from re-routing under the overlay
    void loadInto(wrap, link.dataset.legal!, company);
  });

  void loadInto(wrap, slug, company);
}

async function loadInto(
  wrap: HTMLElement,
  slug: string,
  company: () => Promise<CompanyInfo | null>,
): Promise<void> {
  const title = wrap.querySelector<HTMLElement>('.lglv-title')!;
  const body = wrap.querySelector<HTMLElement>('.lglv-body')!;
  title.textContent = '';
  body.innerHTML = '<p class="lglv-wait">Fetching the paperwork&hellip;</p>';
  try {
    const [page, info] = await Promise.all([fetchLegalPage(slug), company()]);
    title.textContent = page.title;
    body.innerHTML = fillCompanyVars(page.content, info);
    wrap.querySelector<HTMLElement>('.lglv-doc')?.scrollTo({ top: 0 });
  } catch {
    title.textContent = 'Not on file';
    body.innerHTML =
      '<p class="lglv-wait">This document could not be retrieved right now. ' +
      'Try again in a moment, or write to us instead.</p>';
  }
}
